import { responseHeaderVersionDiffers } from '@shared/axios/helpers';
import type { ErrorComponentProps } from '@tanstack/react-router';
import axios from 'axios';
import { useEffect } from 'react';

import { useMessages } from '@/hooks/messages';
import { useShowNewVersionDeployedOnce } from '@/hooks/useShowNewVersionDeployedOnce';
import { reportCrash } from '@/services/crashReporting';

import { componentErrorMessageDefinitions as genericErrorMessageDefinitions } from './messages';

export const GenericError = (props: ErrorComponentProps) => {
  const messages = useMessages(genericErrorMessageDefinitions);
  const { showNewVersionDeployedMessage } = useShowNewVersionDeployedOnce();

  // Report the crash to App Insights (EWM-954). This component is all the user sees of
  // a render failure, so if it does not report, nothing does.
  useEffect(() => {
    reportCrash(props.error, {
      source: 'react-error-boundary',
      componentStack: props.info?.componentStack,
    });
  }, [props.error, props.info]);

  // Try to check if the error is caused by a new version just deployed.
  useEffect(() => {
    if (props.error.message.includes('Failed to fetch dynamically imported module')) {
      void axios.get('/index.html').then(({ headers }) => {
        if (responseHeaderVersionDiffers(headers)) {
          showNewVersionDeployedMessage();
        }
      });
    }
  }, [props, showNewVersionDeployedMessage]);

  const error = props.error instanceof Error ? props.error : new Error(String(props.error));

  return (
    <div className='m-5 flex flex-col items-center gap-5'>
      {messages.errorOccured()}
      {import.meta.env.DEV && (
        <div className='w-full max-w-[1200px] rounded bg-black/20 p-4 text-left text-xs text-white'>
          <div className='mb-2 font-semibold'>Debug details</div>
          <pre className='break-words whitespace-pre-wrap'>{error.message}</pre>
          {error.stack && <pre className='mt-2 break-words whitespace-pre-wrap'>{error.stack}</pre>}
        </div>
      )}
    </div>
  );
};
